import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, List, ListItem, ListItemText, CircularProgress, Divider, Button } from '@mui/material';
import { backendUrl } from '../config';

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
const API_URL = backendUrl;

export default function PictureApp() {
  // Ouvre automatiquement une image si filePath est présent dans localStorage
  React.useEffect(() => {
    const filePath = window.localStorage.getItem('notepadApp_filePath');
    if (filePath) {
      let folder = '/';
      let fileName = filePath;
      if (filePath.includes('/')) {
        folder = '/' + filePath.split('/').slice(0, -1).join('/');
        fileName = filePath.split('/').pop();
      }
      setExplorerPath(folder);
      setSelectedImage({ name: fileName, type: 'file' });
      window.localStorage.removeItem('notepadApp_filePath');
    }
  }, []);
  const [explorerPath, setExplorerPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [zoom, setZoom] = useState(1.0);

  // Charger les fichiers du dossier courant
  useEffect(() => {
    async function fetchFiles() {
      setLoading(true);
      setError('');
      try {
        const userStr = window.localStorage.getItem('user');
        let uuid7 = null;
        if (userStr) {
          try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
        }
        if (!uuid7) throw new Error('Utilisateur non trouvé');
        const token = window.localStorage.getItem('token');
        let relPath = explorerPath === '/' ? '' : explorerPath.replace(/^\//, '');
        // Toujours utiliser l'URL backend
        const filesUrl = `${API_URL.replace(/\/$/, '')}/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`;
        const res = await fetch(filesUrl, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        });
        if (!res.ok) throw new Error('Erreur lors du chargement du dossier');
        const data = await res.json();
        setFiles(data);
      } catch (e) {
        setError(e.message || 'Erreur de chargement');
        setFiles([]);
      } finally {
        setLoading(false);
      }
    }
    fetchFiles();
  }, [explorerPath]);

  // Charger l'image sélectionnée
  useEffect(() => {
    if (!selectedImage) { setImageUrl(null); setZoom(1.0); return; }
    async function fetchImage() {
      setImageUrl(null);
      try {
        const userStr = window.localStorage.getItem('user');
        let uuid7 = null;
        if (userStr) {
          try { uuid7 = JSON.parse(userStr).uuid7 || JSON.parse(userStr).uuid || JSON.parse(userStr).id; } catch (e) {}
        }
        const token = window.localStorage.getItem('token');
        let relPath = explorerPath === '/' ? selectedImage.name : explorerPath.replace(/^\//, '') + '/' + selectedImage.name;
        // Toujours utiliser l'URL backend
        const fileUrl = `${API_URL.replace(/\/$/, '')}/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`;
        setImageUrl(fileUrl);
        setZoom(1.0); // reset zoom à chaque nouvelle image
      } catch {}
    }
    fetchImage();
  }, [selectedImage, explorerPath]);

  // Navigation
  function handleNavigate(folder) {
    setExplorerPath(explorerPath === '/' ? `/${folder}` : `${explorerPath}/${folder}`);
  }
  function handleGoUp() {
    if (explorerPath === '/') return;
    const parts = explorerPath.split('/').filter(Boolean);
    parts.pop();
    setExplorerPath(parts.length === 0 ? '/' : '/' + parts.join('/'));
  }

  return (
    <Box sx={{ height: '100vh', width: '100vw', display: 'flex', flexDirection: 'row' }}>
      <Paper elevation={3} sx={{ width: 320, height: '100vh', borderRadius: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid #ddd', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Images</Typography>
          <Box flex={1} />
          <Button size="small" onClick={handleGoUp} disabled={explorerPath === '/'}>↑</Button>
        </Box>
        {loading ? <CircularProgress sx={{ m: 2 }} /> : error ? <Typography color="error" sx={{ m: 2 }}>{error}</Typography> : (
          <List dense>
            {files.filter(f => f.type === 'folder').map(folder => (
              <ListItem button key={folder.name} onClick={() => handleNavigate(folder.name)}>
                <ListItemText primary={folder.name} secondary="Dossier" />
              </ListItem>
            ))}
            <Divider sx={{ my: 1 }} />
            {files.filter(f => f.type === 'file' && IMAGE_EXT.includes((f.name.split('.').pop() || '').toLowerCase())).map(file => (
              <ListItem button key={file.name} selected={selectedImage && selectedImage.name === file.name} onClick={() => setSelectedImage(file)}>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        )}
      </Paper>
      <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f7f7f7' }}>
        {selectedImage && imageUrl ? (
          <Box sx={{ p: 2, border: '1px solid #ccc', borderRadius: 2, bgcolor: '#fff', maxWidth: '90vw', maxHeight: '90vh', boxShadow: 2 }}>
            <Typography variant="subtitle1" align="center" sx={{ mb: 1 }}>{selectedImage.name}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img
                src={imageUrl}
                alt={selectedImage.name}
                style={{
                  maxWidth: '80vw',
                  maxHeight: '75vh',
                  display: 'block',
                  margin: 'auto',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.2s',
                  border: '1px solid #eee',
                  boxShadow: '0 2px 8px #0002',
                  background: '#fafafa'
                }}
              />
            </Box>
            <Typography align="center" sx={{ mt: 1 }} color="textSecondary">Zoom&nbsp;: {(zoom*100).toFixed(0)}%</Typography>
          </Box>
        ) : (
          <Typography color="textSecondary">Sélectionnez une image pour l'afficher</Typography>
        )}
        {/* Boutons de zoom en bas, centrés */}
        {selectedImage && imageUrl && (
          <Box sx={{ position: 'fixed', left: 0, right: 0, bottom: 32, display: 'flex', justifyContent: 'center', zIndex: 1200 }}>
            <Button variant="outlined" size="large" sx={{ mx: 1 }} onClick={() => setZoom(z => Math.max(0.2, z - 0.2))}>-</Button>
            <Button variant="outlined" size="large" sx={{ mx: 1 }} onClick={() => setZoom(z => Math.min(3.0, z + 0.2))}>+</Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}